'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import TextScrollFadeIn from '@/components/global/partials/TextScrollFadeIn';
import { GroqImage } from '@/helpers/Image';
import { PageCareerOverviewSchema } from '@/sanity/pages/PageCareerOverviewTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function CareerIntroSection({ data }: { data: PageCareerOverviewSchema }) {
  if (!data.introTitle && !data.introText) return null;

  const hasAuthor = data.introAuthorName || data.introAuthorImage;

  return (
    <section className="w-full bg-white px-[2rem] pb-[16.75rem] lg:px-[6rem] lg:pb-[12.5rem]">
      <div className="mx-auto flex w-[min(169.5rem,100%)] flex-col items-center">
        {data.introLogo && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease }}
            className="h-[12rem] w-[11.5rem]"
          >
            <GroqImage src={data.introLogo} alt="" quality={100} className="!h-full !w-full !object-contain" />
          </motion.div>
        )}

        {data.introTitle && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext mt-[3rem] w-full prose-headings:mb-0 prose-headings:text-[4.5rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.3rem] max-lg:[&_h2]:[text-wrap:wrap] max-lg:[&_.font-gestura]:!text-[#003828] lg:mt-[4rem] lg:prose-headings:text-[6.5rem] lg:prose-headings:tracking-[-0.325rem]"
          >
            <PortableText value={data.introTitle} components={richTextComponent} />
          </motion.div>
        )}

        {data.introText && (
          <TextScrollFadeIn
            portableText={data.introText}
            className="richtext mt-[2.5rem] w-full max-w-[123.5rem] prose-p:mb-0 prose-p:[text-wrap:pretty] lg:prose-p:[text-wrap:balance] prose-p:text-[2.8rem] prose-p:font-normal prose-p:leading-[1.31] prose-p:tracking-[-0.08rem] prose-p:text-black lg:mt-[2.5rem] lg:prose-p:text-[4rem] lg:prose-p:leading-[1.31]"
          />
        )}

        {hasAuthor && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="flex w-full max-w-[70rem] flex-col items-center max-lg:mt-[3rem] lg:mt-[4rem]"
          >
            <span aria-hidden="true" className="h-px w-[calc(100%+4rem)] bg-black/10 lg:w-full" />
            <div className="mt-[3rem] flex items-center gap-[1.75rem] lg:mt-[4rem] lg:gap-[2rem]">
              {data.introAuthorImage && (
                <div className="h-[7.75rem] w-[7.75rem] shrink-0 overflow-hidden lg:h-[7.5rem] lg:w-[7.5rem]">
                  <GroqImage
                    src={data.introAuthorImage}
                    alt={data.introAuthorName || ''}
                    quality={100}
                    className="!h-full !w-full !object-cover"
                  />
                </div>
              )}
              <div className="flex flex-col">
                {data.introAuthorName && (
                  <p className="mb-0 text-[2.8rem] font-semibold leading-[1.3] text-black lg:text-[3rem] lg:leading-[1.2]">
                    {data.introAuthorName}
                  </p>
                )}
                {data.introAuthorRole && (
                  <p className="mb-0 font-geist-mono text-[2rem] uppercase leading-[1.4] text-black/50 lg:text-[2rem] lg:leading-[1]">
                    {data.introAuthorRole}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
